import React from 'react';

interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  width?: string;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
}

export function Table<T extends { id: string }>({
  columns,
  data,
  emptyMessage = 'No data',
  onRowClick,
}: TableProps<T>) {
  if (data.length === 0) {
    return (
      <div
        style={{
          padding: '48px 24px',
          textAlign: 'center',
          color: 'var(--neutral-500)',
          fontSize: 14,
        }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          minWidth: columns.length > 3 ? 720 : undefined,
          borderCollapse: 'collapse',
          fontSize: 14,
        }}
      >
        <thead>
          <tr style={{ background: 'var(--neutral-200)' }}>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--neutral-600)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  width: col.width,
                  borderBottom: '1px solid var(--neutral-300)',
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.id}
              onClick={() => onRowClick?.(row)}
              style={{
                background: i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)',
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'background 150ms ease',
              }}
              onMouseEnter={(e) => {
                if (onRowClick) e.currentTarget.style.background = 'var(--blue-100)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)';
              }}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    padding: '14px 16px',
                    color: 'var(--neutral-800)',
                    borderBottom: '1px solid var(--neutral-300)',
                  }}
                >
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
